import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import NavBar from './components/NavBar.jsx';
// The quests page (its cards, the gallery and the traced region emblems) is its own download: the
// home page fetches it once idle, and a click towards it fetches it at once, while the old page
// fades out.
const loadQuests = () => import('./components/Quests.jsx');
const Quests = lazy(loadQuests);
const PAGE_LOADERS = { '/quests': loadQuests };
import Self from './components/Self.jsx';
import Contact from './components/Contact.jsx';
const ExperienceLab = lazy(() => import('./components/ExperienceLab.jsx'));
const RiderShaderLab = lazy(() => import('./components/shaderLab/RiderShaderLab.jsx'));
const KitsuneLab = lazy(() => import('./components/KitsuneLab.jsx'));
const Avalon = lazy(() => import('./components/Avalon.jsx'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard.jsx'));
import SceneBackground from './components/SceneBackground.jsx';
import { MOBILE_SCENE_QUERY, experienceStage } from './components/experience/experienceStage.js';
import { NavFadeCover } from './components/sceneMirror.jsx';
import FrameMeter from './components/FrameMeter.jsx';
import { PERFORMANCE_PATH, SHOW_FRAME_METER } from './components/frameStats.js';
import { pageScrollTo } from './components/pageScroll.js';
import { holdLoader, releaseLoader, setStage } from './bootLoader.js';

// Dev only: bake the 3D-off kitsune stills (components/experience/bakeKitsuneStills.js).
if (import.meta.env.DEV) {
  window.__bakeKitsuneStills = () => import('./components/experience/bakeKitsuneStills.js').then((bake) => bake.bakeKitsuneStills());
}

import { Analytics } from "@vercel/analytics/react"
// framer-motion's lightweight components (m, imported as motion) with just the animation features
// the faders use (opacity, transforms, exit), instead of the full motion components. Strict, so a
// full `motion` component (which would bring every feature back) throws rather than slipping in.
// Layout animations or drag aren't in domAnimation: they need domMax here first.
import { LazyMotion, domAnimation } from 'framer-motion';
import { usePersistentAnalytics } from './persistentAnalytics.js';

// Route changes fade the page out, swap it while it's invisible (back at the top), then fade the
// new page in. One persistent <main> carries the fade, so a page never appears before its fade-in
// starts or blinks as it ends (mounting a fresh animated element per page did both).
const PAGE_FADE_MS = 350;
// Only these paths render pages. Redirect-only and unknown paths have no page to fade out.
const PAGE_PATHS = new Set([
  '/self', '/quests', '/contact', '/analytics', '/lab/experience',
  '/lab/shaders', '/lab/kitsune', '/avalon',
]);
const isRedirect = (path) => !PAGE_PATHS.has(path);

// /lab/performance/<page>: the page, with the frame meter on for the tab (frameStats.js).
function PerformanceView() {
  const { '*': page } = useParams();
  return <Navigate to={`/${page || 'self'}`} replace />;
}

function FadeRoutes() {
  const location = useLocation();
  const [shown, setShown] = useState(location);
  const [leaving, setLeaving] = useState(false);
  const mainRef = useRef(null);
  const scrollerRef = useRef(null);
  // The page's scroller needs the backdrop's copy for its glass, except the phone quests page's,
  // which isn't masked (its own scrolling area is, with its own copy).
  const [phone, setPhone] = useState(() => window.matchMedia(MOBILE_SCENE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(MOBILE_SCENE_QUERY);
    const change = () => setPhone(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  // Desktop pages scroll in .page-scroller (pageScroll.js): focused, so the keyboard scrolls it.
  useEffect(() => { scrollerRef.current.focus({ preventScroll: true }); }, []);

  // The scene behind switches with the address, as the old page starts fading, not once it's gone:
  // the kitsune behind the quests, the Lawson store everywhere else.
  useEffect(() => {
    experienceStage.show = location.pathname === '/quests' ? 'kitsune' : 'lawson';
    PAGE_LOADERS[location.pathname]?.();
  }, [location.pathname]);

  // The quests page's code, in behind the loading screen, so going there later doesn't wait on it.
  useEffect(() => {
    holdLoader('quests code', 0.3);
    loadQuests().finally(() => releaseLoader('quests code'));
    // The first render is up: one frame for it to paint, then the loading screen stops waiting on it.
    const frame = window.requestAnimationFrame(() => releaseLoader('app'));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (location.pathname === shown.pathname || isRedirect(shown.pathname)) {
      // Same page (a hash or search change), or arriving through a redirect: no fade.
      if (location !== shown) setShown(location);
      setLeaving(false);
      return undefined;
    }
    setLeaving(true);
    // Swap once the fade-out has actually finished (a busy frame can start it late), with a
    // timer in case the transition never runs (reduced motion, an already-invisible page).
    const main = mainRef.current;
    let fallback = 0;
    const swap = () => {
      main.removeEventListener('transitionend', onFaded);
      window.clearTimeout(fallback);
      pageScrollTo({ top: 0, behavior: 'instant' });
      setShown(location);
      setLeaving(false);
    };
    const onFaded = (event) => {
      if (event.target === main && event.propertyName === 'opacity') swap();
    };
    main.addEventListener('transitionend', onFaded);
    fallback = window.setTimeout(swap, PAGE_FADE_MS * 3);
    return () => {
      main.removeEventListener('transitionend', onFaded);
      window.clearTimeout(fallback);
    };
  }, [location]);

  return (
    <>
    <div className="page-scroller" ref={scrollerRef} tabIndex={-1}>
    <main
      ref={mainRef}
      className={`main-content${shown.pathname === '/avalon' ? ' main-content--archive' : ''}`}
      style={{ opacity: leaving ? 0 : 1, transition: `opacity ${PAGE_FADE_MS}ms ease-in-out` }}
    >
      <Routes location={shown}>
        <Route path="/" element={<Navigate to="/self" replace />} />
        <Route path="/self" element={<Self />} />
        <Route path="/quests" element={<Suspense fallback={<PageLoading />}><Quests /></Suspense>} />
        {/* The gallery's projects are the World Quests now. */}
        <Route path="/gallery" element={<Navigate to="/quests" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/analytics" element={<Suspense fallback={<PageLoading />}><AnalyticsDashboard /></Suspense>} />
        <Route path="/lab/experience" element={<Suspense fallback={<PageLoading />}><ExperienceLab /></Suspense>} />
        <Route path="/lab/shaders" element={<Suspense fallback={<PageLoading />}><RiderShaderLab /></Suspense>} />
        <Route path="/lab/kitsune" element={<Suspense fallback={<PageLoading />}><KitsuneLab /></Suspense>} />
        <Route path={`${PERFORMANCE_PATH}/*`} element={<PerformanceView />} />
        <Route path="/avalon" element={<Suspense fallback={<PageLoading><div style={{ minHeight: '100vh', background: '#060f21' }} /></PageLoading>}><Avalon /></Suspense>} />

        <Route path="*" element={<Navigate to="/self" replace />} />
      </Routes>
    </main>
    </div>
    {/* The fade under the menu bar, over the page (sceneMirror.jsx); the phone quests page fades its own area. */}
    <NavFadeCover active={!(phone && shown.pathname === '/quests')} />
    </>
  );
}

// A page whose code is still coming holds the loading screen (bootLoader.js), so arriving on it
// shows the loading screen, then the page, never an empty frame between. Once the loading
// screen is gone (moving between pages), it holds nothing.
function PageLoading({ children = null }) {
  useEffect(() => {
    holdLoader('page code');
    setStage('page code');
    return () => releaseLoader('page code');
  }, []);
  return children;
}

function SiteChrome() {
  const { pathname } = useLocation();
  usePersistentAnalytics();
  if (pathname === '/avalon' || pathname === '/lab/kitsune' || pathname === '/analytics') return null;
  return <><SceneBackground /><div className="blossom-atmosphere" aria-hidden="true" /><NavBar /></>;
}

export default function App() {
  return (
    <LazyMotion features={domAnimation} strict>
    <Router>
      <div className="app-container" id="popup-root">
        <SiteChrome />
        <FadeRoutes />
        {SHOW_FRAME_METER && <FrameMeter />}
      </div>
        <Analytics />
    </Router>
    </LazyMotion>
  );
}
